import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { X, Plus, Trash2, UserCheck, Upload, FileText, CheckCircle2, Loader2, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import InlineContactSearch from '../InlineContactSearch';
import TrimbleAddressAutocomplete from '../TrimbleAddressAutocomplete';
import ContactFormModal from './ContactFormModal';
import { cn } from '@/lib/utils';
import { computeLoadGross } from '@/lib/invoiceHelpers';
import { calcRoute } from '@/lib/pcmiler';
import { checkBookingEligibility } from '@/lib/driverEligibility';

const SectionHeader = ({ title }) => (
  <p className="text-xs font-semibold text-primary uppercase tracking-widest pb-1 border-b border-border">{title}</p>
);

const emptyLoad = () => ({
  load_number: '', trip_number: '', origin: '', destination: '', miles: '',
  equipment_required: 'Dry Van', commodity: '', weight: '',
  pickup_date: '', pickup_time_window: '', delivery_date: '', dropoff_time_window: '',
  pickup_address: '', dropoff_address: '', po_pickup_number: '',
  special_notes: '', notes: '',
  trailer_size: '', pallet_case_count: '', temperature: '', hazmat: false, load_requirements: '',
  line_haul_rate: '', line_haul_rate_type: 'Flat Fee', accessorial_pay: '',
  fuel_surcharge: 0, fuel_surcharge_type: 'Flat Fee',
  detention_fee: 0, lumper_fee: 0, late_fee: 0, stop_off_fee: 0, tarp_fee: 0, layover_fee: 0,
  additional_fee: 0, additional_fee_label: '',
  extra_fees: '',
  invoice_advance: 0,
  legal_disclaimer: '',
  status: 'Available',
  assigned_driver_email: '',
  assigned_driver_name: '',
  assignment_status: null,
  customer_name: '',
  customer_email: '',
  customer_contact_id: '',
  shipper_name: '', shipper_contact: '', shipper_phone: '', shipper_email: '',
  shipper_contact_id: '',
  consignee_name: '', consignee_contact: '', consignee_phone: '', consignee_email: '',
  consignee_contact_id: '',
});

export default function NewLoadModal({ onClose, onCreated }) {
  const { user } = useAuth();
  const [form, setForm] = useState(emptyLoad());
  const [stops, setStops] = useState([]);
  const [approvedDrivers, setApprovedDrivers] = useState([]);
  const [truckByEmail, setTruckByEmail] = useState({});
  const [trucks, setTrucks] = useState([]);
  const [assignedTruckId, setAssignedTruckId] = useState('');
  const [saving, setSaving] = useState(false);
  const [rateconFile, setRateconFile] = useState(null);
  const [uploadingRatecon, setUploadingRatecon] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [rateconError, setRateconError] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactFormType, setContactFormType] = useState(null);
  const [contactFormSearch, setContactFormSearch] = useState('');
  const [milesVerified, setMilesVerified] = useState(false);
  const [pickupAddressError, setPickupAddressError] = useState('');
  const [dropoffAddressError, setDropoffAddressError] = useState('');
  const [customerError, setCustomerError] = useState('');
  const [extraFees, setExtraFees] = useState([]);
  const [rateConLoading, setRateConLoading] = useState(false);
  const [autoFilledFields, setAutoFilledFields] = useState(new Set());

  useEffect(() => {
    client.entities.DriverProfile.filter({ is_active: true }, 'full_name', 100)
      .then(setApprovedDrivers)
      .catch(() => {});

    // Map each driver to the truck unit # they're currently assigned to
    client.entities.Asset.filter({ asset_type: 'Truck' }, '-created_date', 200)
      .then(list => {
        const active = list.filter(t => t.is_active !== false);
        const map = {};
        active.forEach(t => {
          if (t.assigned_driver_email) map[t.assigned_driver_email.toLowerCase()] = t.unit_number;
        });
        setTruckByEmail(map);
        setTrucks(active);
      })
      .catch(() => {});

    // Trip Number default: next sequential number starting at 1001,
    // same convention as Load # -- previously always blank, so every
    // load required someone to type one in by hand. Still fully
    // editable; this is just a sane starting default.
    client.entities.Load.list('-created_date', 500).then(all => {
      const nums = all.map(l => parseInt(l.trip_number, 10)).filter(n => !isNaN(n));
      const next = nums.length ? Math.max(...nums) + 1 : 1001;
      setForm(f => (f.trip_number ? f : { ...f, trip_number: String(next) }));
    }).catch(() => {});
  }, []);

  const set = (key, value) => setForm(p => ({ ...p, [key]: value }));

  const buildAddressString = (street, city, state, zip) => {
    const parts = [street, city, state, zip].filter(Boolean);
    return parts.join(', ');
  };

  // Shared by both mileage-calc triggers below -- they only differ in
  // whether an unrecognized address should surface a visible warning
  // (validateAndCalcMiles, on manual entry) or fail silently
  // (triggerMileCalc, right after a confident autocomplete pick).
  //
  // `overrides` matters: every call site invokes this synchronously right
  // after one or more set(...) calls in the same handler. React batches
  // state updates, so `form` here is still the PRE-update snapshot --
  // reading e.g. form.dropoff_zip right after set('dropoff_zip', ...) in
  // the same tick sees the OLD (often empty) value, not the one just
  // picked. That silently failed the `!pickup_zip || !dropoff_zip` guard
  // below on whichever address was completed last, so the mileage calc
  // never actually ran (confirmed: zero real invocations reached the
  // network despite users completing both addresses). Callers pass the
  // fields they just set as `overrides` so this always sees current data.
  const calcMiles = async ({ showErrors, overrides = {} } = {}) => {
    const f = { ...form, ...overrides };
    if (!f.pickup_zip || !f.dropoff_zip) return;
    // Additional Stops (drag-reorderable, below) ride between the
    // primary pickup and dropoff in visit order -- previously this
    // only ever routed a straight pickup->dropoff line, so Miles
    // (and the Est. driving distance it feeds) silently ignored every
    // additional stop's real detour. A stop with no city/state yet
    // (still being typed in) is skipped rather than breaking the route.
    const stopPoints = stops
      .filter(s => s.city && s.state)
      .map(s => ({ street: s.street, city: s.city, state: s.state, zip: s.zip }));
    const result = await calcRoute(
      [
        { street: f.pickup_street, city: f.pickup_city, state: f.pickup_state, zip: f.pickup_zip },
        ...stopPoints,
        { street: f.dropoff_street, city: f.dropoff_city, state: f.dropoff_state, zip: f.dropoff_zip },
      ],
      { hazmat: f.hazmat }
    );
    if (result.error || !(result.miles > 0)) {
      const msg = result.error || 'Address not recognized by PCMiler — please verify.';
      if (showErrors) {
        setPickupAddressError(`⚠ ${msg}`);
        setDropoffAddressError(`⚠ ${msg}`);
      } else {
        console.error('Miles calc failed:', msg);
        toast.error(msg, { duration: 10000 });
      }
      return;
    }
    set('miles', String(Math.round(result.miles)));
    setMilesVerified(true);
    if (showErrors) {
      setPickupAddressError('');
      setDropoffAddressError('');
    }
  };

  const triggerMileCalc = (overrides) => calcMiles({ showErrors: false, overrides });
  const validateAndCalcMiles = (overrides) => calcMiles({ showErrors: true, overrides });

  // Recalculates Miles whenever a stop's address changes OR the stops
  // are reordered (drag-and-drop, below) -- keyed off just the
  // address-relevant fields in array order, so editing a stop's
  // Company Name or PO # (unrelated to the route) doesn't refire this
  // on every keystroke. Runs after the stops state has actually
  // committed (an effect, not an inline call at each edit site), so it
  // always sees the true final array -- reordering/adding/removing a
  // stop needs no separate trigger call of its own.
  const stopsRouteSignature = JSON.stringify(stops.map(s => [s.city, s.state, s.zip]));
  useEffect(() => {
    if (form.pickup_zip && form.dropoff_zip) triggerMileCalc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopsRouteSignature]);

  const handleDriverSelect = (email) => {
    if (!email) {
      set('assigned_driver_email', '');
      set('assigned_driver_name', '');
      set('status', 'Available');
      set('assignment_status', null);
      setAssignedTruckId('');
      return;
    }
    const driver = approvedDrivers.find(d => d.user_email === email);
    const { eligible, reasons } = checkBookingEligibility(driver, email, trucks);
    if (!eligible) {
      toast.error(`Can't book ${driver?.full_name || email} — ${reasons.join(' ')}`, { duration: 6000 });
      return;
    }
    set('assigned_driver_email', email);
    set('assigned_driver_name', driver?.full_name || '');
    set('status', 'Booked');
    set('assignment_status', null);
    // Default to the truck this driver is already on, if any
    const curTruck = trucks.find(t => (t.assigned_driver_email || '').toLowerCase() === email.toLowerCase());
    setAssignedTruckId(curTruck?.id || '');
    // Auto-fill Equipment/Trailer Size from whatever this driver is
    // actually running -- a Company Driver's trailer_type/trailer_size is
    // synced here from their assigned Trailer Asset (AssetsTab.jsx), an
    // Owner Operator's is self-reported on their own profile. Previously
    // a dispatcher had to already know (or go ask) and type it in by
    // hand every single time.
    if (driver?.trailer_type) set('equipment_required', driver.trailer_type);
    if (driver?.trailer_size) set('trailer_size', driver.trailer_size);
  };

  const handleRateConUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRateConLoading(true);
    try {
      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result.split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });

      // Upload file for ratecon attachment
      const { file_url } = await client.integrations.Core.UploadFile({ file });
      setForm(prev => ({ ...prev, ratecon_url: file_url }));
      setRateconFile(file);
      setRateconError(false);

      // Parse with AI
      const response = await client.functions.invoke('parseRateCon', { pdfBase64: base64 });
      const result = response.data;
      if (result && Object.keys(result).length > 0) {
        // parseRateCon's extract_rate_confirmation tool returns
        // pickup_street/pickup_city/pickup_state/pickup_zip (and the
        // dropoff_* equivalents) already split out, matching this form's
        // own field names exactly -- no remapping needed. additional_stops
        // is handled separately below since it doesn't map onto a single
        // form field.
        const { additional_stops, ...loadFields } = result;
        const filledKeys = Object.keys(loadFields).filter(k => loadFields[k] !== '' && loadFields[k] !== 0 && loadFields[k] != null);
        setForm(prev => ({ ...prev, ...loadFields }));
        // Rate cons with more than one pickup or delivery: the AI reports
        // everything beyond the single primary pickup/dropoff (above) as
        // additional_stops, one entry per extra stop -- replaces whatever
        // stops were already on the form, same as re-uploading a rate con
        // replaces the rest of the auto-filled fields.
        if (Array.isArray(additional_stops) && additional_stops.length > 0) {
          setStops(additional_stops.map(s => ({
            _key: crypto.randomUUID(),
            company: s.company || '',
            street: s.street || '',
            city: s.city || '',
            state: s.state || '',
            zip: s.zip || '',
            po_number: s.po_number || '',
          })));
        }
        setMilesVerified(!!loadFields.miles && loadFields.miles > 0);
        // Address autocomplete and InlineContactSearch both trigger the
        // PCMiler mileage calc the moment an address is picked (see
        // triggerMileCalc calls throughout this file) -- this flow set
        // pickup_zip/dropoff_zip above but never did, so Miles stayed
        // permanently blank after a rate con upload unless the document
        // itself happened to state a total mileage figure. Trigger it
        // here too, whenever the AI didn't already extract a usable one.
        if (!(loadFields.miles && loadFields.miles > 0) && loadFields.pickup_zip && loadFields.dropoff_zip) {
          triggerMileCalc({
            pickup_street: loadFields.pickup_street,
            pickup_city: loadFields.pickup_city,
            pickup_state: loadFields.pickup_state,
            pickup_zip: loadFields.pickup_zip,
            dropoff_street: loadFields.dropoff_street,
            dropoff_city: loadFields.dropoff_city,
            dropoff_state: loadFields.dropoff_state,
            dropoff_zip: loadFields.dropoff_zip,
          });
        }
        setAutoFilledFields(new Set(filledKeys));
        // Clear highlight after 3 seconds
        setTimeout(() => setAutoFilledFields(new Set()), 3000);
        toast.success('Rate con parsed — please review and confirm the fields.');
      } else {
        toast.error('Could not extract data from this PDF. Please fill in manually.');
      }
    } catch (err) {
      toast.error('Failed to parse rate con.');
    } finally {
      setRateConLoading(false);
      e.target.value = '';
    }
  };

  const addStop = () => setStops(s => [...s, { _key: crypto.randomUUID(), company: '', street: '', city: '', state: '', zip: '', po_number: '' }]);
  const removeStop = (i) => setStops(s => s.filter((_, idx) => idx !== i));
  const updateStop = (i, key, value) => setStops(s => s.map((stop, idx) => idx === i ? { ...stop, [key]: value } : stop));
  const handleStopDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    setStops(s => {
      const next = Array.from(s);
      const [moved] = next.splice(result.source.index, 1);
      next.splice(result.destination.index, 0, moved);
      return next;
    });
  };



  const handleCreate = async () => {
    if (!form.customer_name?.trim()) {
      setCustomerError('Customer is required');
      toast.error('Customer is required.');
      return;
    }
    if (!form.line_haul_rate) {
      toast.error('Line haul rate is required.');
      return;
    }
    if (form.pickup_date && form.delivery_date && form.delivery_date < form.pickup_date) {
      setDateError(true);
      toast.error('Delivery date must be after the pickup date.');
      return;
    }
    setDateError(false);
    if (!rateconFile) {
      toast.error('A Rate Confirmation document is required to post a load.');
      setRateconError(true);
      return;
    }
    setRateconError(false);
    setSaving(true);
    setUploadingRatecon(true);
    // Previously had no try/catch at all -- a failure anywhere in this
    // flow (upload, save, geocoding) threw and left setSaving(true)
    // stuck forever with zero feedback: the button just sat on
    // "Submitting..." indefinitely with no error shown, no way to
    // retry short of closing the modal and starting over.
    try {
      const { file_url: ratecon_url } = await client.integrations.Core.UploadFile({ file: rateconFile });
      setUploadingRatecon(false);
      const lineHaul = parseFloat(form.line_haul_rate) || 0;
      const accessorial = parseFloat(form.accessorial_pay) || 0;
      const pickup_address = buildAddressString(form.pickup_street, form.pickup_city, form.pickup_state, form.pickup_zip);
      const dropoff_address = buildAddressString(form.dropoff_street, form.dropoff_city, form.dropoff_state, form.dropoff_zip);
      // Only rebuild from city/state when both are actually present --
      // otherwise fall back to whatever origin/destination the form
      // already has (e.g. from rate con auto-fill) instead of clobbering
      // it with a literal "undefined, undefined" string.
      const origin = (form.pickup_city && form.pickup_state) ? `${form.pickup_city}, ${form.pickup_state}` : (form.origin || '');
      const destination = (form.dropoff_city && form.dropoff_state) ? `${form.dropoff_city}, ${form.dropoff_state}` : (form.destination || '');
      // Snapshot which unit is actually hauling this load at booking time --
      // unit-level reports (UnitRevenueReport, UnitMilesReport, etc.) used
      // to infer this from "whichever truck is CURRENTLY assigned to this
      // driver," which silently misattributes historical loads once a
      // driver's truck is reassigned. truck_unit already exists as a plain-
      // text field for CSV-imported loads; this just populates it going
      // forward for loads booked through the app too.
      const assignedTruckUnit = assignedTruckId ? trucks.find(t => t.id === assignedTruckId)?.unit_number : undefined;
      await client.entities.Load.create({
        ...form,
        pickup_address,
        dropoff_address,
        origin,
        destination,
        truck_unit: assignedTruckUnit || form.truck_unit || undefined,
        // pickup_date/delivery_date are real `date`-typed columns --
        // Postgres rejects an empty string with a 400 (this is what was
        // silently killing every submission with no pickup/delivery
        // date filled in, before the try/catch above existed to even
        // surface it). undefined omits the key entirely instead.
        pickup_date: form.pickup_date || undefined,
        delivery_date: form.delivery_date || undefined,
        // customer/shipper/consignee_contact_id are uuid-typed columns --
        // same empty-string-rejection issue as the dates above. These
        // default to '' until a contact is actually picked from
        // InlineContactSearch, and were being spread straight from `form`
        // (see the ...form spread below) with no coercion, so every load
        // saved without an exact contact match failed with "invalid input
        // syntax for type uuid: ''".
        customer_contact_id: form.customer_contact_id || undefined,
        shipper_contact_id: form.shipper_contact_id || undefined,
        consignee_contact_id: form.consignee_contact_id || undefined,
        line_haul_rate: lineHaul,
        accessorial_pay: accessorial,
        rate: grossRate,
        miles: form.miles ? parseFloat(form.miles) : undefined,
        load_number: form.load_number || `L${Date.now().toString().slice(-5)}`,
        created_by: user?.id || null,
        // Stops are edited as structured street/city/state/zip fields
        // (see the Additional Stops block above), but ActiveLoad.jsx's
        // driver-facing stop display still reads a single `address`
        // string per stop -- compute it here so that keeps working
        // without every reader needing to know about the structured
        // fields too.
        additional_stops: stops.length > 0
          ? JSON.stringify(stops.map(({ _key, ...s }) => ({ ...s, address: buildAddressString(s.street, s.city, s.state, s.zip) })))
          : '',
        ratecon_url,
        extra_fees: extraFees.length > 0 ? JSON.stringify(extraFees) : '',
      });
      // Keep the truck Asset in sync so the owner portal & dispatch reflect who's
      // driving it (Asset.assigned_driver_email is the source of truth elsewhere).
      if (assignedTruckId && form.assigned_driver_email) {
        const t = trucks.find(x => x.id === assignedTruckId);
        if (t && (t.assigned_driver_email || '').toLowerCase() !== form.assigned_driver_email.toLowerCase()) {
          try {
            await client.entities.Asset.update(assignedTruckId, { assigned_driver_email: form.assigned_driver_email });
          } catch {
            toast.error("Load saved, but updating the truck's assigned driver failed — set it in the Assets tab.");
          }
        }
      }
      toast.success('Load posted successfully!');
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to post load. Please try again.', { duration: 8000 });
    } finally {
      setUploadingRatecon(false);
      setSaving(false);
    }
  };

  const grossRate = computeLoadGross(form);

  const field = (label, key, type = 'text', options = null) => {
    const highlighted = autoFilledFields.has(key);
    const highlightClass = highlighted ? 'border-primary/60 ring-1 ring-primary/40' : '';
    return (
      <div>
        <label className="text-xs text-muted-foreground block mb-1">{label}</label>
        {options ? (
          <select
            value={form[key]}
            onChange={e => set(key, e.target.value)}
            className={cn("w-full px-3 py-2 rounded-lg bg-transparent border text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50", highlighted ? 'border-primary/60 ring-1 ring-primary/40' : 'border-input')}
          >
            {options.map(o => <option key={o} value={o} className="bg-background">{o}</option>)}
          </select>
        ) : (
          <Input
            type={type}
            value={form[key]}
            onChange={e => set(key, e.target.value)}
            className={cn(type === 'number' ? "w-full px-3 py-2 rounded-lg bg-transparent border text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50" : "text-foreground", highlighted ? 'border-primary/60 ring-1 ring-primary/40' : type === 'number' ? 'border-input' : '')}
          />
        )}
      </div>
    );
  };

  const textarea = (label, key) => (
    <div>
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      <textarea
        value={form[key]}
        onChange={e => set(key, e.target.value)}
        rows={2}
        className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary/50"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="flex min-h-full items-start justify-center p-4">
        <div className="relative w-full max-w-3xl my-8 p-6 space-y-5 z-10 rounded-2xl border border-border bg-background">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-space font-semibold text-foreground text-lg">Create Load</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Fill in the details to create a new load in the TMS</p>
          </div>
          <div className="flex items-center gap-2">
            <label className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium cursor-pointer transition-colors",
              rateConLoading
                ? "bg-primary/10 border-primary/20 text-primary/50 cursor-not-allowed pointer-events-none"
                : "bg-primary/20 border-primary/40 text-primary hover:bg-primary/30"
            )}>
              {rateConLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Reading Rate Con...</>
              ) : (
                <><Upload className="w-4 h-4" /> Auto Fill From Rate Con</>
              )}
              <input type="file" accept=".pdf" className="hidden" onChange={handleRateConUpload} disabled={rateConLoading} />
            </label>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Customer */}
        <div>
          <SectionHeader title="Customer / Broker" />
          <div className="mt-3">
            <label className="text-xs text-muted-foreground block mb-1.5">Customer / Broker *</label>
            <InlineContactSearch
              value={form.customer_name}
              onChange={e => { set('customer_name', e); set('customer_contact_id', ''); setCustomerError(''); }}
              contactType="Customer"
              onSelect={data => {
                set('customer_name', data.name);
                set('customer_email', data.email);
                set('customer_contact_id', data.id || '');
              }}
              onShowAddForm={(type, search) => {
                setContactFormType(type);
                setContactFormSearch(search);
                setShowContactForm(true);
              }}
              placeholder="Search or add customer"
            />
            {customerError && <p className="text-xs text-red-400 mt-1">{customerError}</p>}
            <label className="text-xs text-muted-foreground block mb-1.5 mt-3">Email</label>
            <Input
              type="email"
              value={form.customer_email}
              onChange={e => set('customer_email', e.target.value)}
              placeholder="Customer email"
            />
          </div>
        </div>

        {/* Assign Driver */}
        <div>
          <SectionHeader title="Driver Assignment" />
          <div className="mt-3">
            <label className="text-xs text-muted-foreground block mb-1">Assign to Driver (Optional)</label>
            <div className="relative">
              <UserCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <select
                value={form.assigned_driver_email}
                onChange={e => handleDriverSelect(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm"
              >
                <option value="" className="bg-background text-muted-foreground">Leave Unassigned (Post to Load Board)</option>
                {approvedDrivers.map(d => (
                  <option key={d.id} value={d.user_email} className="bg-background">
                    {d.full_name} — {truckByEmail[d.user_email?.toLowerCase()] ? `Unit ${truckByEmail[d.user_email?.toLowerCase()]}` : 'No truck assigned'}
                  </option>
                ))}
              </select>
            </div>
            {form.assigned_driver_email && (
              <>
                <div className="mt-3">
                  <label className="text-xs text-muted-foreground block mb-1">Truck / Unit (syncs this driver onto the truck)</label>
                  <select
                    value={assignedTruckId}
                    onChange={e => setAssignedTruckId(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm"
                  >
                    <option value="" className="bg-background text-muted-foreground">— No truck / leave as is —</option>
                    {trucks.map(t => (
                      <option key={t.id} value={t.id} className="bg-background">
                        Unit {t.unit_number} — {[t.year, t.make, t.model].filter(Boolean).join(' ') || 'Truck'}{t.assigned_driver_email && t.assigned_driver_email.toLowerCase() !== form.assigned_driver_email.toLowerCase() ? ` · now: ${t.assigned_driver_email}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-amber-400 mt-1.5">Status will be set to <strong>Booked</strong> automatically.{assignedTruckId ? ' This driver will be set as the truck’s assigned driver.' : ''}</p>
              </>
            )}
          </div>
        </div>

        {/* Routing & Schedule */}
        <div>
          <div className="mt-3 space-y-4">
            {/* Row 1: Load Number / Trip Number */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {field('Load Number', 'load_number')}
              {field('Trip Number', 'trip_number')}
            </div>

            {/* Shipper Section */}
            <div className="mb-4 p-4 rounded-xl bg-muted/40 border border-border space-y-3">
              <SectionHeader title="Shipper" />
              <div className="mb-3 mt-3">
                <InlineContactSearch
                  value={form.shipper_name}
                  onChange={e => { set('shipper_name', e); set('shipper_contact_id', ''); }}
                  contactType="Shipper"
                  onSelect={data => {
                    set('shipper_name', data.name);
                    set('shipper_contact', data.contact);
                    set('shipper_phone', data.phone);
                    set('shipper_email', data.email);
                    set('shipper_contact_id', data.id || '');
                    set('pickup_street', data.street);
                    set('pickup_city', data.city);
                    set('pickup_state', data.state);
                    set('pickup_zip', data.zip);
                    validateAndCalcMiles({ pickup_street: data.street, pickup_city: data.city, pickup_state: data.state, pickup_zip: data.zip });
                  }}
                  onShowAddForm={(type, search) => {
                    setContactFormType(type);
                    setContactFormSearch(search);
                    setShowContactForm(true);
                  }}
                  placeholder="Search or add shipper"
                />
              </div>
              <div className="grid grid-cols-[1fr_1fr_120px] gap-2">
                <Input
                  value={form.shipper_contact}
                  onChange={e => set('shipper_contact', e.target.value)}
                  placeholder="Contact Person"
                  className="text-foreground"
                />
                <Input
                  value={form.shipper_phone}
                  onChange={e => set('shipper_phone', e.target.value)}
                  placeholder="Phone"
                  className="text-foreground"
                />
                <Input
                  value={form.shipper_email}
                  onChange={e => set('shipper_email', e.target.value)}
                  placeholder="Email"
                  type="email"
                  className="text-foreground"
                />
              </div>
            </div>

            {/* Row 2: Pickup Address */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <label className="text-xs font-semibold text-foreground">Pickup Address</label>
              </div>
              <div className="mb-2">
                <TrimbleAddressAutocomplete
                  value={form.pickup_street}
                  onChange={e => { set('pickup_street', e.target.value); setPickupAddressError(''); }}
                  onSelect={data => {
                    set('pickup_city', data.city || '');
                    set('pickup_state', data.state || '');
                    set('pickup_zip', data.zip || '');
                    triggerMileCalc({ pickup_city: data.city || '', pickup_state: data.state || '', pickup_zip: data.zip || '' });
                  }}
                  label=""
                  placeholder="Street address"
                />
              </div>
              <div className="grid grid-cols-[1fr_120px_100px] gap-2">
                <Input
                  value={form.pickup_city}
                  onChange={e => { set('pickup_city', e.target.value); setPickupAddressError(''); }}
                  placeholder="City"
                  className="text-foreground"
                />
                <Input
                  value={form.pickup_state}
                  onChange={e => { set('pickup_state', e.target.value); setPickupAddressError(''); }}
                  placeholder="State"
                  className="text-foreground"
                  maxLength="2"
                />
                <Input
                  value={form.pickup_zip}
                  onChange={e => { set('pickup_zip', e.target.value); setPickupAddressError(''); }}
                  placeholder="ZIP"
                  className="text-foreground"
                  onBlur={() => triggerMileCalc()}
                />
              </div>
              {pickupAddressError && <p className="text-xs text-red-400 mt-1">{pickupAddressError}</p>}
            </div>

            {/* Consignee Section */}
            <div className="mb-4 p-4 rounded-xl bg-muted/40 border border-border space-y-3">
              <SectionHeader title="Consignee" />
              <div className="mb-3 mt-3">
                <InlineContactSearch
                  value={form.consignee_name}
                  onChange={e => { set('consignee_name', e); set('consignee_contact_id', ''); }}
                  contactType="Consignee"
                  onSelect={data => {
                    set('consignee_name', data.name);
                    set('consignee_contact', data.contact);
                    set('consignee_phone', data.phone);
                    set('consignee_email', data.email);
                    set('consignee_contact_id', data.id || '');
                    set('dropoff_street', data.street);
                    set('dropoff_city', data.city);
                    set('dropoff_state', data.state);
                    set('dropoff_zip', data.zip);
                    validateAndCalcMiles({ dropoff_street: data.street, dropoff_city: data.city, dropoff_state: data.state, dropoff_zip: data.zip });
                  }}
                  onShowAddForm={(type, search) => {
                    setContactFormType(type);
                    setContactFormSearch(search);
                    setShowContactForm(true);
                  }}
                  placeholder="Search or add consignee"
                />
              </div>
              <div className="grid grid-cols-[1fr_1fr_120px] gap-2">
                <Input
                  value={form.consignee_contact}
                  onChange={e => set('consignee_contact', e.target.value)}
                  placeholder="Contact Person"
                  className="text-foreground"
                />
                <Input
                  value={form.consignee_phone}
                  onChange={e => set('consignee_phone', e.target.value)}
                  placeholder="Phone"
                  className="text-foreground"
                />
                <Input
                  value={form.consignee_email}
                  onChange={e => set('consignee_email', e.target.value)}
                  placeholder="Email"
                  type="email"
                  className="text-foreground"
                />
              </div>
            </div>

            {/* Row 3: Drop-off Address */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <label className="text-xs font-semibold text-foreground">Drop-off Address</label>
              </div>
              <div className="mb-2">
                <TrimbleAddressAutocomplete
                  value={form.dropoff_street}
                  onChange={e => { set('dropoff_street', e.target.value); setDropoffAddressError(''); }}
                  onSelect={data => {
                    set('dropoff_city', data.city || '');
                    set('dropoff_state', data.state || '');
                    set('dropoff_zip', data.zip || '');
                    triggerMileCalc({ dropoff_city: data.city || '', dropoff_state: data.state || '', dropoff_zip: data.zip || '' });
                  }}
                  label=""
                  placeholder="Street address"
                />
              </div>
              <div className="grid grid-cols-[1fr_120px_100px] gap-2">
                <Input
                  value={form.dropoff_city}
                  onChange={e => { set('dropoff_city', e.target.value); setDropoffAddressError(''); }}
                  placeholder="City"
                  className="text-foreground"
                />
                <Input
                  value={form.dropoff_state}
                  onChange={e => { set('dropoff_state', e.target.value); setDropoffAddressError(''); }}
                  placeholder="State"
                  className="text-foreground"
                  maxLength="2"
                />
                <Input
                  value={form.dropoff_zip}
                  onChange={e => { set('dropoff_zip', e.target.value); setDropoffAddressError(''); }}
                  placeholder="ZIP"
                  className="text-foreground"
                  onBlur={() => triggerMileCalc()}
                />
              </div>
              {dropoffAddressError && <p className="text-xs text-red-400 mt-1">{dropoffAddressError}</p>}
            </div>

            {/* Row 4: Pickup Date | Pickup Time Window | Delivery Date */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={cn("text-xs block mb-1", dateError ? "text-red-400" : "text-muted-foreground")}>Pickup Date</label>
                <Input type="date" value={form.pickup_date} onChange={e => { set('pickup_date', e.target.value); setDateError(false); }} className={cn("text-foreground", dateError ? "border-red-500 ring-1 ring-red-500" : "")} />
              </div>
              {field('Pickup Time Window', 'pickup_time_window')}
              <div>
                <label className={cn("text-xs block mb-1", dateError ? "text-red-400" : "text-muted-foreground")}>Delivery Date</label>
                <Input type="date" value={form.delivery_date} onChange={e => { set('delivery_date', e.target.value); setDateError(false); }} className={cn("text-foreground", dateError ? "border-red-500 ring-1 ring-red-500" : "")} />
                {dateError && <p className="text-xs text-red-400 mt-1">Delivery must be after pickup date.</p>}
              </div>
            </div>

            {/* Row 5: Drop-off Time Window (1/3) | Miles (2/3) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {field('Drop-off Time Window', 'dropoff_time_window')}
              <div className="sm:col-span-2">
                <label className="text-xs text-muted-foreground block mb-1">Miles</label>
                <div className="relative">
                  <Input
                    type="number"
                    value={form.miles}
                    onChange={e => { set('miles', e.target.value); setMilesVerified(false); }}
                    readOnly
                    className="text-foreground cursor-default"
                    placeholder="Auto-filled when both cities selected"
                  />
                  {milesVerified && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-green-400 text-xs font-medium pointer-events-none">
                      <CheckCircle2 className="w-3.5 h-3.5" /> PC*Miler ✓
                    </span>
                  )}
                </div>
              </div>
            </div>

            {field('PO / Pickup Number', 'po_pickup_number')}
          </div>
        </div>

        {/* Dynamic Additional Stops */}
        <div>
          <SectionHeader title="Additional Stops" />
          {stops.length > 1 && (
            <p className="text-xs text-muted-foreground mt-2">Drag stops into the order they're actually visited — Miles recalculates to match.</p>
          )}
          <DragDropContext onDragEnd={handleStopDragEnd}>
            <Droppable droppableId="additional-stops">
              {(droppableProvided) => (
                <div className="mt-3 space-y-3" ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                  {stops.map((stop, i) => (
                    <Draggable key={stop._key} draggableId={stop._key} index={i}>
                      {(draggableProvided, draggableSnapshot) => (
                        <div
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          className={cn(
                            "relative p-4 rounded-xl bg-muted/40 border border-border space-y-3",
                            draggableSnapshot.isDragging && "ring-2 ring-primary/50 shadow-xl"
                          )}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-1.5">
                              <span
                                {...draggableProvided.dragHandleProps}
                                className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground -ml-1 p-0.5 touch-none"
                                title="Drag to reorder"
                              >
                                <GripVertical className="w-4 h-4" />
                              </span>
                              <span className="text-xs font-semibold text-primary">Stop {i + 1}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeStop(i)}
                              className="text-red-400 hover:text-red-300 transition-colors p-1 rounded hover:bg-red-500/10"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          {/* Mirrors the Pickup/Drop-off Address blocks above (street
                              autocomplete + city/state/zip grid) instead of a single
                              free-text address field, so an additional stop looks and
                              behaves the same as the primary pickup/drop-off. */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-muted-foreground block mb-1">Company Name</label>
                              <Input value={stop.company} onChange={e => updateStop(i, 'company', e.target.value)} className="text-foreground" placeholder="e.g. Walmart DC" />
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground block mb-1">PO #</label>
                              <Input value={stop.po_number} onChange={e => updateStop(i, 'po_number', e.target.value)} className="text-foreground" placeholder="PO-XXXXXX" />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground block mb-1">Street Address</label>
                            <TrimbleAddressAutocomplete
                              value={stop.street}
                              onChange={e => updateStop(i, 'street', e.target.value)}
                              onSelect={data => {
                                updateStop(i, 'city', data.city || '');
                                updateStop(i, 'state', data.state || '');
                                updateStop(i, 'zip', data.zip || '');
                              }}
                              label=""
                              placeholder="Street address"
                            />
                          </div>
                          <div className="grid grid-cols-[1fr_120px_100px] gap-2">
                            <Input value={stop.city} onChange={e => updateStop(i, 'city', e.target.value)} placeholder="City" className="text-foreground" />
                            <Input value={stop.state} onChange={e => updateStop(i, 'state', e.target.value)} placeholder="State" className="text-foreground" maxLength="2" />
                            <Input value={stop.zip} onChange={e => updateStop(i, 'zip', e.target.value)} placeholder="ZIP" className="text-foreground" />
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <button
            type="button"
            onClick={addStop}
            className="w-full py-3 mt-3 rounded-xl border-2 border-dashed border-primary/50 text-primary text-sm font-semibold hover:bg-primary/5 transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Stop
          </button>
        </div>

        {/* Special Notes */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {textarea('Special Notes', 'special_notes')}
            {textarea('Internal Notes', 'notes')}
          </div>
        </div>

        {/* Freight Specifications */}
        <div className="space-y-4">
          <SectionHeader title="Freight Specifications" />
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {field('Equipment', 'equipment_required', 'text', ['Dry Van', 'Reefer'])}
              {field('Trailer Size (e.g. 53 ft)', 'trailer_size')}
              {field('Pallet / Case Count', 'pallet_case_count')}
              {field('Temperature', 'temperature')}
              {field('Commodity', 'commodity')}
              {field('Weight', 'weight')}
            </div>
            {textarea('Load Requirements (e.g. Drop Trailer, Power Only)', 'load_requirements')}
          </div>
        </div>

        {/* Fees & Charges */}
        <div className="space-y-4">
          <SectionHeader title="Fees & Charges" />
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Primary Fee */}
              <div className="space-y-4">
                <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide">Primary Fee</h4>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Line Haul Rate Type</label>
                    <select
                      value={form.line_haul_rate_type || 'Flat Fee'}
                      onChange={e => set('line_haul_rate_type', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                    >
                      <option value="Flat Fee" className="bg-background">Flat Fee</option>
                      <option value="Per Mile" className="bg-background">Per Mile</option>
                      <option value="Percent" className="bg-background">Percent</option>
                    </select>
                  </div>
                  {field('Line Haul Rate ($)', 'line_haul_rate', 'number')}
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">FSC Amount Type</label>
                    <select
                      value={form.fuel_surcharge_type || 'Flat Fee'}
                      onChange={e => set('fuel_surcharge_type', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                    >
                      <option value="Flat Fee" className="bg-background">Flat Fee</option>
                      <option value="Per Mile" className="bg-background">Per Mile</option>
                      <option value="Percent" className="bg-background">Percent</option>
                    </select>
                  </div>
                  {field('Fuel Surcharge ($)', 'fuel_surcharge', 'number')}
                </div>
              </div>

              {/* Accessory Fees */}
              <div className="space-y-4">
                <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide">Accessory Fees</h4>
                <div className="space-y-4">
                  {field('Detention ($)', 'detention_fee', 'number')}
                  {field('Layover ($)', 'layover_fee', 'number')}
                  {field('Stop Off ($)', 'stop_off_fee', 'number')}
                  {field('Tarp Fee ($)', 'tarp_fee', 'number')}
                  {field('Lumper ($)', 'lumper_fee', 'number')}
                  {field('Late Fee ($)', 'late_fee', 'number')}
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Additional Fee Label</label>
                    <Input
                      value={form.additional_fee_label || ''}
                      onChange={e => set('additional_fee_label', e.target.value)}
                      placeholder="e.g. Dock Fee"
                      className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                  </div>
                  {field('Additional Fee ($)', 'additional_fee', 'number')}
                  {field('Invoice Advance ($)', 'invoice_advance', 'number')}
                </div>
              </div>
            </div>

            {/* Extra Fees */}
            {extraFees.length > 0 && (
              <div className="p-4 rounded-xl bg-muted/40 border border-border space-y-3">
                {extraFees.map((fee, i) => (
                  <div key={i} className="flex gap-3 items-end">
                    <Input
                      value={fee.label}
                      onChange={e => {
                        const updated = [...extraFees];
                        updated[i].label = e.target.value;
                        setExtraFees(updated);
                        set('extra_fees', JSON.stringify(updated));
                      }}
                      placeholder="Fee label"
                      className="flex-1 px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                    <Input
                      type="number"
                      value={fee.amount}
                      onChange={e => {
                        const updated = [...extraFees];
                        updated[i].amount = parseFloat(e.target.value) || 0;
                        setExtraFees(updated);
                        set('extra_fees', JSON.stringify(updated));
                      }}
                      placeholder="0.00"
                      className="w-24 px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const updated = extraFees.filter((_, idx) => idx !== i);
                        setExtraFees(updated);
                        set('extra_fees', JSON.stringify(updated));
                      }}
                      className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 text-xs font-medium"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                const updated = [...extraFees, { label: '', amount: 0 }];
                setExtraFees(updated);
                set('extra_fees', JSON.stringify(updated));
              }}
              className="px-3 py-2 rounded-lg bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 text-xs font-medium"
            >
              + Add Custom Fee
            </button>


          </div>
        </div>

        {/* Rate Breakdown */}
        <div className="space-y-4">
          <SectionHeader title="Rate Breakdown" />
          <div className="space-y-4">
            <div className="flex justify-between items-center px-4 py-3 rounded-xl bg-primary/10 border border-primary/20">
              <span className="text-sm font-semibold text-foreground">Gross Rate (Auto-calculated)</span>
              <span className="font-bold text-primary text-lg">
                ${grossRate.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        {/* Rate Confirmation Upload */}
        <div className="space-y-4">
          <SectionHeader title="Rate Confirmation (Required)" />
          <div className="space-y-4">
            <label className={cn(
              "flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition-colors",
              rateconFile ? "border-primary/60 bg-primary/5" : rateconError ? "border-red-500 bg-red-500/5" : "border-border hover:border-primary/40 hover:bg-white/3"
            )} onClick={() => setRateconError(false)}>
              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={e => { setRateconFile(e.target.files[0]); setRateconError(false); }} />
              {rateconFile ? <FileText className="w-5 h-5 text-primary flex-shrink-0" /> : <Upload className={cn("w-5 h-5 flex-shrink-0", rateconError ? "text-red-400" : "text-muted-foreground")} />}
              <div>
                <p className={cn("text-sm font-medium", rateconFile ? "text-primary" : rateconError ? "text-red-400" : "text-muted-foreground")}>
                  {rateconFile ? rateconFile.name : 'Upload Rate Confirmation (PDF / JPG / PNG)'}
                </p>
                {!rateconFile && <p className={cn("text-xs mt-0.5", rateconError ? "text-red-400 font-semibold" : "text-muted-foreground")}>{rateconError ? '⚠ Rate Confirmation is required to post this load' : 'Required before posting the load'}</p>}
              </div>
            </label>
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <Button onClick={handleCreate} disabled={saving} className="flex-1 bg-primary text-primary-foreground">
            {uploadingRatecon ? 'Uploading Rate Con...' : saving ? 'Submitting...' : 'Submit'}
          </Button>
          <Button onClick={onClose} variant="outline" className="border-border">Cancel</Button>
        </div>
      </div>

      {showContactForm && (
        <ContactFormModal
          contact={{
            name: contactFormSearch,
            type: contactFormType === 'Customer' ? 'Customer' : contactFormType,
          }}
          onClose={() => setShowContactForm(false)}
          onSaved={(newContact) => {
            if (newContact && newContact.name) {
              if (contactFormType === 'Customer') {
                set('customer_name', newContact.name);
                set('customer_email', newContact.email);
                set('customer_contact_id', newContact.id || '');
              } else if (contactFormType === 'Shipper') {
                set('shipper_name', newContact.name);
                set('shipper_contact', newContact.contact);
                set('shipper_phone', newContact.primary_phone);
                set('shipper_email', newContact.email);
                set('shipper_contact_id', newContact.id || '');
                set('pickup_street', newContact.street);
                set('pickup_city', newContact.city);
                set('pickup_state', newContact.state);
                set('pickup_zip', newContact.zip_code);
                validateAndCalcMiles({ pickup_street: newContact.street, pickup_city: newContact.city, pickup_state: newContact.state, pickup_zip: newContact.zip_code });
              } else if (contactFormType === 'Consignee') {
                set('consignee_name', newContact.name);
                set('consignee_contact', newContact.contact);
                set('consignee_phone', newContact.primary_phone);
                set('consignee_email', newContact.email);
                set('consignee_contact_id', newContact.id || '');
                set('dropoff_street', newContact.street);
                set('dropoff_city', newContact.city);
                set('dropoff_state', newContact.state);
                set('dropoff_zip', newContact.zip_code);
                validateAndCalcMiles({ dropoff_street: newContact.street, dropoff_city: newContact.city, dropoff_state: newContact.state, dropoff_zip: newContact.zip_code });
              }
            }
            setShowContactForm(false);
          }}
        />
      )}
        </div>
      </div>
  );
}
